/**
 * Laravel Echo WebSocket utilities
 * Uses Laravel Echo with pusher-js against a Reverb server (Pusher protocol).
 */

import Echo from 'laravel-echo'
import Pusher from 'pusher-js'
import { runtimeEnv } from 'src/CrudManager/Core/Utils/runtimeConfig'
import { useUserStore } from 'src/CrudManager/Core/stores/useUserStore'

declare global {
  interface Window {
    Pusher: typeof Pusher
  }
}

window.Pusher = Pusher

/**
 * Global Laravel Echo socket instance
 * Singleton instance that is reused across the application
 * Set to null initially and created on first getSocket() call
 *
 * @public
 */
export let echoSocket: Echo<'pusher'> | null = null

/**
 * Gets or creates the Laravel Echo WebSocket instance
 * Creates a new Echo instance on first call and reuses it on subsequent calls
 * Returns null if user is not authenticated (no token available)
 * Configures Echo (pusher-js driver) with authentication headers and connection event logging
 *
 * @returns Laravel Echo instance or null if user is not authenticated
 * @public
 * @example
 * ```typescript
 * const socket = getSocket()
 * if (socket) {
 *   socket.channel('my-channel').listen('.MyEvent', (data) => {
 *     console.log('Event received:', data)
 *   })
 * }
 * ```
 */
export const getSocket = () => {
  const userStore = useUserStore()
  if (!userStore.token) {
    return null
  }

  if (!echoSocket && userStore.token) {
    const configuredHost = runtimeEnv('VITE_REVERB_HOST', import.meta.env.VITE_REVERB_HOST)
    const publicScheme = runtimeEnv('VITE_REVERB_SCHEME', import.meta.env.VITE_REVERB_SCHEME)
    const publicPort = runtimeEnv('VITE_REVERB_PORT', import.meta.env.VITE_REVERB_PORT)
    // Without a configured host, Reverb shares the page's origin (under /app/).
    const { host, scheme, portRaw } = configuredHost
      ? { host: configuredHost, scheme: publicScheme || import.meta.env.REVERB_SCHEME || 'https', portRaw: publicPort || import.meta.env.REVERB_PORT }
      : { host: window.location.hostname, scheme: publicScheme || window.location.protocol.replace(/:$/, ''), portRaw: publicPort || window.location.port }
    const portNum = portRaw !== undefined && portRaw !== '' ? Number(portRaw) : undefined

    echoSocket = new Echo({
      broadcaster: 'pusher',
      key: runtimeEnv('REVERB_APP_KEY', import.meta.env.REVERB_APP_KEY),
      wsHost: host,
      wsPort: portNum ?? 80,
      wssPort: portNum ?? 443,
      cluster: 'mt1',
      forceTLS: scheme === 'https',
      encrypted: true,
      disableStats: true,
      enabledTransports: ['ws', 'wss'],

      channelAuthorization: {
        endpoint: '/api/broadcasting/auth',
        headers: { Authorization: `Bearer ${userStore.token}` },
      },
    })

    // Log when socket is connected
    echoSocket.connector.pusher.connection.bind('connected', () => {
      console.log('WebSocket connected')
    })

    // Log when socket is disconnected
    echoSocket.connector.pusher.connection.bind('disconnected', () => {
      console.log('WebSocket disconnected')
    })

    // Log any errors
    echoSocket.connector.pusher.connection.bind('error', (error: Error) => {
      console.error('WebSocket error:', error)
    })
  }
  return echoSocket
}
